import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, InputSignal, signal, ViewEncapsulation, WritableSignal } from '@angular/core';
import { Image, ModalGalleryService, ModalGalleryRef, ModalGalleryConfig, ModalLibConfig } from '@ks89/angular-modal-gallery';
import { TranslateModule } from '@ngx-translate/core';
import { DebugService } from 'src/app/services/debug.service';
import { ToolsService } from 'src/app/services/tools.service';
import { SwiperModule } from 'swiper/angular';

@Component({
    selector: 'details-hero',
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        SwiperModule
    ],
    templateUrl: './hero.component.html',
    styleUrl: './hero.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})

export class DetailsHeroComponent {
  public itemType = input.required<string>();
  public categoryNameSlug: InputSignal<string> = input.required<string>();
  public defaultFileImgName: InputSignal<string> = input.required<string>();
  public items: InputSignal<any[]> = input.required<any[]>();
  public isBottomSheet: InputSignal<any> = input<any>(false);
  private modalGalleryService = inject(ModalGalleryService)
  private debugService = inject(DebugService);
  public tools = inject(ToolsService);
  public multimedia = signal(null);
  public showSwiper: WritableSignal<boolean> = signal(false);
  public galleryImages: any[] = [];
  
  private _imageCounter = 0; // Counter for cycling through 1-4
  private _currentNameSlug = ''; // Track current category to reset counter
  private _itemImageMap = new Map<string, number>(); // Cache images per item

  ngOnInit(){
    this.galleryImages = [];
    let imageCount = 0;

    this.multimedia.set(this.items()?.map(element => {
      if (element.type === 'image'){ 
        const image: Image = new Image(imageCount, { img: element.name });
        element.imageIndex = imageCount;
        this.galleryImages[imageCount] = image;
        imageCount++;
      }
      return element;
    }))

    setTimeout(() => {
        this.showSwiper.set(true);
    }, 750);
  }

  openModal(id: number, imagesArrayToUse: Image[], imageIndex: number, libConfig?: ModalLibConfig): void {
    if(this.isBottomSheet()) return;
    
    if(imagesArrayToUse.length === 0) {
        this.debugService.error('[GallerySliderComponent] Images array empty');
        return;
    }
    if(imageIndex > imagesArrayToUse.length - 1) {
        this.debugService.error('[GallerySliderComponent] imageIndex not valid');
        return;
    }
    const imageToShow: Image = imagesArrayToUse[imageIndex];
    const dialogRef: ModalGalleryRef = this.modalGalleryService.open({
        id,
        images: imagesArrayToUse,
        currentImage: imageToShow,
        libConfig
    } as ModalGalleryConfig) as ModalGalleryRef;
  }
  
  public getDefaultImgFileName(index: number): string {
    // Convert index to 1-4 cycle: 0->1, 1->2, 2->3, 3->4, 4->1, 5->2, etc.
    const cyclicIndex = (index % 4) + 1;
    
    return this.itemType() === 'event' 
            ? this.getEventDefaultImgFileName(cyclicIndex) 
            : this.getInterestPointDefaultImgFileName(cyclicIndex);
  }
  
  public getEventDefaultImgFileName(index: number): string {
    // Create unique key for this item
    const itemKey = `event-${index}`;
    
    // Return cached image if exists
    if (this._itemImageMap.has(itemKey)) {
      const imageNumber = this._itemImageMap.get(itemKey)!;
      return `/assets/imgs/default/event/event-${imageNumber}.webp`;
    }
    
    // Increment counter and cycle between 1-4 (NO reset per item)
    this._imageCounter = (this._imageCounter % 4) + 1;
    
    // Cache the image number for this item
    this._itemImageMap.set(itemKey, this._imageCounter);
    
    return `/assets/imgs/default/event/event-${this._imageCounter}.webp`;
  }

  public getInterestPointDefaultImgFileName(index: number): string {
    // Create unique key for this item
    const itemKey = `${this.categoryNameSlug()}-${index}`;
    
    // Return cached image if exists
    if (this._itemImageMap.has(itemKey)) {
      const imageNumber = this._itemImageMap.get(itemKey)!;
      return `/assets/imgs/default/category/${this.categoryNameSlug()}/${this.categoryNameSlug()}-${imageNumber}.webp`;
    }
    
    // Reset counter if nameSlug changed
    if (this._currentNameSlug !== this.categoryNameSlug()) {
      this._currentNameSlug = this.categoryNameSlug();
      this._imageCounter = 0;
    }
    
    // Increment counter and cycle between 1-4
    this._imageCounter = (this._imageCounter % 4) + 1;
    
    // Cache the image number for this item
    this._itemImageMap.set(itemKey, this._imageCounter);

    return `/assets/imgs/default/category/${this.categoryNameSlug()}/${this.categoryNameSlug()}-${this._imageCounter}.webp`;
  }
} 